<template>
  <div class="tender-quotes-panel">
    <!-- Panel Header -->
    <div class="panel-header">
      <div class="row items-center">
        <div class="col">
          <div class="panel-title">
            <q-icon name="description" color="secondary" class="q-mr-sm" />
            Tender Quotes
          </div>
          <div class="panel-subtitle">
            {{ quotes.length }} quote{{ quotes.length !== 1 ? 's' : '' }} created for this tender
          </div>
        </div>
        <div class="col-auto">
          <q-btn
            color="secondary"
            icon="add"
            label="Create Quote"
            class="add-btn"
            @click="$emit('add-quote')"
          />
        </div>
      </div>
    </div>

    <!-- Quotes List -->
    <div v-if="quotes.length === 0" class="empty-state">
      <q-icon name="description" size="4em" color="grey-5" />
      <div class="empty-title">No Quotes Created</div>
      <div class="empty-subtitle">
        Create quotes to estimate project costs and prepare tender documents
      </div>
      <q-btn
        color="secondary"
        icon="add"
        label="Create First Quote"
        class="q-mt-md"
        @click="$emit('add-quote')"
      />
    </div>

    <div v-else>
      <div class="quotes-grid">
        <div
          v-for="quote in quotes"
          :key="quote.id"
          class="quote-card"
        >
          <q-card 
            flat 
            bordered 
            :class="{ 'quote-sections-active': selectedQuoteForSections?.id === quote.id }"
          >
            <!-- Quote Header -->
            <q-card-section class="quote-header">
              <div class="row items-center">
                <div class="col">
                  <div class="quote-name">
                    {{ quote.quote_name || quote.name || `Quote #${quote.quote_id || quote.id}` }}
                    <q-badge
                      v-if="quote.is_primary"
                      color="primary"
                      label="Primary"
                      class="q-ml-sm"
                    />
                  </div>
                  <div class="quote-reference">
                    <q-icon name="tag" size="16px" class="q-mr-xs" />
                    {{ quote.quote_number || quote.reference_number || 'No reference' }}
                  </div>
                </div>
                <div class="col-auto">
                  <q-badge
                    :color="getQuoteStatusColor(quote.status)"
                    :label="getQuoteStatusLabel(quote.status)"
                    class="status-badge"
                  />
                </div>
              </div>
            </q-card-section>

            <!-- Quote Metrics -->
            <q-card-section class="quote-metrics">
              <div class="metrics-row">
                <div class="metric-item">
                  <div class="metric-value">${{ formatCurrency(quote.subtotal || 0) }}</div>
                  <div class="metric-label">Subtotal</div>
                </div>
                
                <div class="metric-item">
                  <div class="metric-value">${{ formatCurrency(quote.tax_amount || 0) }}</div>
                  <div class="metric-label">Tax</div>
                </div>
                
                <div class="metric-item primary-metric">
                  <div class="metric-value">${{ formatCurrency(quote.total_amount || 0) }}</div>
                  <div class="metric-label">Total</div>
                </div>
              </div>
              
              <div class="quote-progress">
                <div class="progress-label">
                  Completion: {{ calculateCompletion(quote) }}%
                </div>
                <q-linear-progress
                  :value="calculateCompletion(quote) / 100"
                  color="secondary"
                  size="8px"
                  rounded
                />
              </div>
            </q-card-section>

            <!-- Quote Details -->
            <q-card-section class="quote-details">
              <div class="details-grid">
                <div class="detail-item">
                  <q-icon name="inventory" size="16px" color="grey-6" />
                  <span class="detail-label">Products:</span>
                  <span class="detail-value">{{ quote.product_count || 0 }} items</span>
                </div>
                
                <div class="detail-item">
                  <q-icon name="build" size="16px" color="grey-6" />
                  <span class="detail-label">Labor:</span>
                  <span class="detail-value">{{ quote.labor_count || 0 }} items</span>
                </div>
                
                <div class="detail-item">
                  <q-icon name="folder" size="16px" color="grey-6" />
                  <span class="detail-label">Documents:</span>
                  <span class="detail-value">{{ quote.documents_count || 0 }} files</span>
                </div>
                
                <div class="detail-item">
                  <q-icon name="edit" size="16px" color="grey-6" />
                  <span class="detail-label">Last Updated:</span>
                  <span class="detail-value">{{ formatDate(quote.updated_at) }}</span>
                </div>
              </div>

              <!-- Quote Description -->
              <div v-if="quote.description" class="quote-description">
                <div class="description-label">
                  <q-icon name="description" size="16px" class="q-mr-xs" />
                  Description
                </div>
                <div class="description-text">{{ quote.description }}</div>
              </div>
            </q-card-section>

            <!-- Quote Actions -->
            <q-card-actions class="quote-actions">
              <q-btn
                flat
                icon="edit"
                label="Edit"
                color="primary"
                size="sm"
                @click="viewQuote(quote)"
              />

              <q-space />

              <q-btn-group flat>
                <q-btn
                  flat
                  round
                  dense
                  icon="content_copy"
                  color="blue-grey"
                  @click="duplicateQuote(quote)"
                >
                  <q-tooltip>Duplicate Quote</q-tooltip>
                </q-btn>

                <q-btn
                  flat
                  round
                  dense
                  icon="delete"
                  color="negative"
                  @click="confirmDelete(quote)"
                >
                  <q-tooltip>Delete Quote</q-tooltip>
                </q-btn>
              </q-btn-group>
            </q-card-actions>

            <!-- Quote Status Progress -->
            <div class="quote-progress-indicator">
              <q-linear-progress
                :value="getQuoteProgress(quote)"
                :color="getQuoteProgressColor(quote)"
                size="3px"
              />
            </div>
          </q-card>
        </div>
      </div>
      
      <!-- Quote Sections Display - Full Width -->
      <div v-if="selectedQuoteForSections" class="quote-sections-full-width q-mt-lg">
        <div class="sections-header">
          <div class="row items-center">
            <div class="col">
              <div class="sections-title">
                <q-icon name="tune" color="primary" class="q-mr-sm" />
                Quote Sections: {{ selectedQuoteForSections.name || `Quote #${selectedQuoteForSections.id}` }}
              </div>
              <div class="sections-subtitle">
                Organize quote items into logical sections for your tender document
              </div>
            </div>
            <div class="col-auto">
              <q-btn
                flat
                round
                icon="close"
                color="grey"
                @click="selectedQuoteForSections = null"
              >
                <q-tooltip>Close Sections</q-tooltip>
              </q-btn>
            </div>
          </div>
        </div>
        
        <div class="sections-content">
          <TenderQuoteSections 
            :tender-id="tenderId" 
            :quote-id="selectedQuoteForSections.quote_id || selectedQuoteForSections.id"
            :embedded="true"
            @manage-items="manageItems"
          />
        </div>
      </div>
    </div>

    <!-- Quotes Summary -->
    <div v-if="quotes.length > 0" class="quotes-summary q-mt-lg">
      <q-card flat bordered>
        <q-card-section>
          <div class="summary-header">
            <q-icon name="analytics" color="accent" class="q-mr-sm" />
            <div class="summary-title">Quote Summary</div>
          </div>
          
          <div class="summary-content">
            <div class="summary-stats">
              <div class="stat-item">
                <div class="stat-number">{{ quotes.length }}</div>
                <div class="stat-label">Total Quotes</div>
              </div>
              
              <div class="stat-item">
                <div class="stat-number">{{ completedQuotes }}</div>
                <div class="stat-label">Completed</div>
              </div>
              
              <div class="stat-item">
                <div class="stat-number">${{ formatCurrency(totalQuoteValue) }}</div>
                <div class="stat-label">Total Value</div>
              </div>
              
              <div class="stat-item">
                <div class="stat-number">${{ formatCurrency(averageQuoteValue) }}</div>
                <div class="stat-label">Average Value</div>
              </div>
            </div>
            
            <!-- Primary Quote Highlight -->
            <div v-if="primaryQuote" class="primary-quote-card">
              <div class="primary-header">
                <q-icon name="star" color="primary" />
                <span class="primary-title">Primary Quote</span>
                <q-space />
                <q-badge color="primary" :label="primaryQuote.status || 'Draft'" />
              </div>
              <div class="primary-content">
                <div class="primary-name">{{ primaryQuote.name || `Quote #${primaryQuote.id}` }}</div>
                <div class="primary-value">${{ formatCurrency(primaryQuote.total_amount || 0) }}</div>
              </div>
            </div>
          </div>
        </q-card-section>
      </q-card>
    </div>

    <!-- Delete Confirmation Dialog -->
    <q-dialog v-model="showDeleteDialog" persistent>
      <q-card>
        <q-card-section class="row items-center">
          <q-avatar icon="warning" color="negative" text-color="white" />
          <span class="q-ml-sm text-h6">Delete Quote</span>
        </q-card-section>
        
        <q-card-section>
          <p>Are you sure you want to delete <strong>{{ quoteToDelete?.name || `Quote #${quoteToDelete?.id}` }}</strong>?</p>
          <p class="text-caption text-grey-7">
            This action cannot be undone. All quote data, products, and labor items will be permanently deleted.
          </p>
        </q-card-section>
        
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" />
          <q-btn 
            label="Delete" 
            color="negative" 
            @click="deleteQuote" 
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue';
  import { useQuasar } from 'quasar';
  import { useRouter } from 'vue-router';
  import TenderQuoteSections from '../TenderQuoteSections.vue';

  const props = defineProps({
    tenderId: {
      type: Number,
      required: true,
    },
    quotes: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits([
    'add-quote',
    'edit-quote',
    'delete-quote',
  ]);

  const $q = useQuasar();
  const router = useRouter();

  // State
  const showDeleteDialog = ref(false);
  const quoteToDelete = ref(null);
  const selectedQuoteForSections = ref(null);

  // Computed properties
  const completedQuotes = computed(() => {
    return props.quotes.filter((q) => q.status === 'completed' || q.status === 'sent').length;
  });

  const totalQuoteValue = computed(() => {
    return props.quotes.reduce((sum, quote) => sum + (Number(quote.total_amount) || 0), 0);
  });

  const averageQuoteValue = computed(() => {
    if (props.quotes.length === 0) {return 0;}
    return totalQuoteValue.value / props.quotes.length;
  });

  const primaryQuote = computed(() => {
    return props.quotes.find((q) => q.is_primary);
  });

  // Methods
  function getQuoteStatusColor(status) {
    const colors = {
      draft: 'grey',
      pending: 'orange',
      completed: 'blue',
      sent: 'positive',
      accepted: 'positive',
      rejected: 'negative',
    };
    return colors[status] || 'grey';
  }

  function getQuoteStatusLabel(status) {
    const labels = {
      draft: 'Draft',
      pending: 'Pending',
      completed: 'Completed',
      sent: 'Sent',
      accepted: 'Accepted',
      rejected: 'Rejected',
    };
    return labels[status] || status || 'Draft';
  }

  function getQuoteProgress(quote) {
    const progressMap = {
      draft: 0.2,
      pending: 0.4,
      completed: 0.7,
      sent: 0.9,
      accepted: 1.0,
      rejected: 1.0,
    };
    return progressMap[quote.status] || 0;
  }

  function getQuoteProgressColor(quote) {
    const colorMap = {
      draft: 'grey',
      pending: 'orange',
      completed: 'blue',
      sent: 'positive',
      accepted: 'positive',
      rejected: 'negative',
    };
    return colorMap[quote.status] || 'grey';
  }

  function calculateCompletion(quote) {
    let completion = 0;
  
    // Base completion for having a quote
    completion += 20;
  
    // Products added
    if (quote.product_count > 0) {completion += 30;}
  
    // Labor added
    if (quote.labor_count > 0) {completion += 20;}
  
    // Description added
    if (quote.description) {completion += 10;}
  
    // Status progression
    if (quote.status === 'completed' || quote.status === 'sent') {completion += 20;}
  
    return Math.min(completion, 100);
  }

  function formatDate(dateString) {
    if (!dateString) {return 'Never';}
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
    });
  }

  function formatCurrency(amount) {
    return Number(amount).toLocaleString('en-AU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  function viewQuote(quote) {
    // Navigate to the quote detail page using the actual quote_id, not the tender_quote relationship id
    const quoteId = quote.quote_id || quote.id;
    router.push(`/quotes/${quoteId}`);
  }

  function toggleQuoteSections(quote) {
    if (selectedQuoteForSections.value && selectedQuoteForSections.value.id === quote.id) {
      // If clicking on the same quote, toggle off
      selectedQuoteForSections.value = null;
    } else {
      // Otherwise, show sections for this quote
      selectedQuoteForSections.value = quote;
    }
  }

  function manageItems(section) {
  
    // For now, show a notification that this feature is available
    $q.notify({
      color: 'info',
      message: `Section items management for "${section.section_name}" will be implemented`,
      timeout: 3000,
    });
  }

  function duplicateQuote(quote) {
    $q.notify({
      color: 'info',
      message: 'Quote duplication functionality will be implemented',
      timeout: 3000,
    });
  }

  function confirmDelete(quote) {
    quoteToDelete.value = quote;
    showDeleteDialog.value = true;
  }

  function deleteQuote() {
    if (quoteToDelete.value) {
      emit('delete-quote', quoteToDelete.value);
      showDeleteDialog.value = false;
      quoteToDelete.value = null;
    }
  }
</script>

<style lang="scss" scoped>
.tender-quotes-panel {
  .panel-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #f0f0f0;
    
    .panel-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #333;
      display: flex;
      align-items: center;
      margin-bottom: 4px;
    }
    
    .panel-subtitle {
      color: #666;
      font-size: 0.9rem;
    }
    
    .add-btn {
      font-weight: 600;
      border-radius: 8px;
    }
  }
  
  .empty-state {
    text-align: center;
    padding: 48px 24px;
    color: #666;
    
    .empty-title {
      font-size: 1.2rem;
      font-weight: 600;
      margin: 16px 0 8px;
    }
    
    .empty-subtitle {
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }
  
  .quotes-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    gap: 20px;
    
    .quote-card {
      position: relative;
      
      .quote-sections-active {
        border-color: #1976d2;
        border-width: 2px;
        box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
      }
      
      .quote-sections-container {
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        background: #f8f9fa;
        padding: 16px;
      }
      
      .quote-header {
        padding-bottom: 12px;
        border-bottom: 1px solid #f0f0f0;
        
        .quote-name {
          font-weight: 700;
          font-size: 1.1rem;
          color: #333;
          display: flex;
          align-items: center;
          margin-bottom: 4px;
        }
        
        .quote-reference {
          font-size: 0.85rem;
          color: #666;
          display: flex;
          align-items: center;
        }
        
        .status-badge {
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.75rem;
        }
      }
      
      .quote-metrics {
        .metrics-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 16px;
          
          .metric-item {
            text-align: center;
            
            .metric-value {
              font-size: 1.2rem;
              font-weight: 700;
              color: #e91e63;
            }
            
            .metric-label {
              font-size: 0.8rem;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            
            &.primary-metric .metric-value {
              font-size: 1.4rem;
              color: #1976d2;
            }
          }
        }
        
        .quote-progress {
          .progress-label {
            font-size: 0.85rem;
            color: #666;
            margin-bottom: 4px;
            font-weight: 500;
          }
        }
      }
      
      .quote-details {
        .details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          margin-bottom: 16px;
          
          .detail-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 0.85rem;
            
            .detail-label {
              color: #666;
              font-weight: 500;
              min-width: 60px;
            }
            
            .detail-value {
              font-weight: 600;
              color: #333;
            }
          }
        }
        
        .quote-description {
          border-top: 1px solid #f0f0f0;
          padding-top: 12px;
          
          .description-label {
            font-size: 0.85rem;
            color: #666;
            font-weight: 500;
            margin-bottom: 6px;
            display: flex;
            align-items: center;
          }
          
          .description-text {
            font-size: 0.9rem;
            color: #333;
            line-height: 1.4;
            font-style: italic;
          }
        }
      }
      
      .quote-actions {
        border-top: 1px solid #f0f0f0;
        padding-top: 8px;
      }
      
      .quote-progress-indicator {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
      }
    }
  }
  
  .quotes-summary {
    .summary-header {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      
      .summary-title {
        font-size: 1.1rem;
        font-weight: 700;
        color: #333;
      }
    }
    
    .summary-content {
      .summary-stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 24px;
        margin-bottom: 24px;
        
        .stat-item {
          text-align: center;
          
          .stat-number {
            font-size: 1.8rem;
            font-weight: 700;
            color: #e91e63;
            margin-bottom: 4px;
          }
          
          .stat-label {
            font-size: 0.85rem;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        }
      }
      
      .primary-quote-card {
        background: rgba(233, 30, 99, 0.05);
        border-radius: 8px;
        padding: 16px;
        border-left: 4px solid #e91e63;
        
        .primary-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          
          .primary-title {
            font-weight: 600;
            color: #e91e63;
          }
        }
        
        .primary-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          
          .primary-name {
            font-weight: 600;
            color: #333;
          }
          
          .primary-value {
            font-weight: 700;
            color: #e91e63;
            font-size: 1.2rem;
          }
        }
      }
    }
  }
  
  .quote-sections-full-width {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    
    .sections-header {
      background: #f8f9fa;
      border-bottom: 1px solid #e0e0e0;
      padding: 16px 20px;
      
      .sections-title {
        font-size: 1.2rem;
        font-weight: 700;
        color: #333;
        display: flex;
        align-items: center;
        margin-bottom: 4px;
      }
      
      .sections-subtitle {
        color: #666;
        font-size: 0.9rem;
        line-height: 1.4;
      }
    }
    
    .sections-content {
      padding: 20px;
      
      // Ensure full width for all child components
      :deep(.quote-sections-wrapper) {
        width: 100%;
        max-width: none;
        
        .q-card {
          width: 100%;
        }
        
        // Fix table layout for proper text wrapping
        .q-table {
          width: 100%;
          
          .q-table__container {
            max-width: 100%;
          }
          
          .q-td, .q-th {
            white-space: normal;
            word-wrap: break-word;
            
            &.ellipsis {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 200px;
            }
          }
        }
        
        // Let Quasar handle list item spacing naturally
        .q-list .q-item {
          padding: 8px 12px;
        }
      }
    }
  }
}
</style>